import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import GridCellErrorBoundary from '../../GridCellErrorBoundary';
import useBonusCellBonusProgram from '../hooks/useBonusCellBonusProgram';
import Cell from '../../V5Gadget/Matrix/Cell';
import Tooltip from '../../Tooltip';
import { StatusIcon } from './BonusStatusCell';
import CloudFunctionClientContext from '../../../contexts/CloudFunctionClientContext';
import resolveDriverRewardOutput from '../utils/resolveDriverRewardOutput';
import useCurrentAccountId from 'hooks/useCurrentAccountId';
import Loading from '../../Loading';
import BonusPeriodsContext from '../../../contexts/BonusPeriodsContext';
import BonusEligibilityTooltip from './BonusEligibility/BonusEligibilityTooltip';

export const QualifiedTooltip = ({
  driver,
  bonusPeriodId,
  bonusProgram,
}: {
  driver: string;
  bonusPeriodId: string;
  bonusProgram: BonusTypes.BonusProgram;
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { selectedSubPeriod } = useContext(BonusPeriodsContext);
  const accountId = useCurrentAccountId();
  const [rewardOutput, setRewardOutput] = useState<
    BonusTypes.Rewards.AnyDriverRewardsOutput | undefined
  >(undefined);

  useEffect(() => {
    let isActive = true;
    resolveDriverRewardOutput({
      api,
      accountId,
      driver,
      bonusProgramId: bonusProgram.id,
      bonusPeriodId,
      subPeriod: selectedSubPeriod,
    }).then((newRewardOutput) => {
      if (isActive && newRewardOutput) {
        setRewardOutput(newRewardOutput);
      }
    });
    return () => {
      isActive = false;
    };
  }, [accountId, api, bonusPeriodId, bonusProgram, driver, selectedSubPeriod]);

  if (!rewardOutput) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          padding: 16,
          border: '1px solid var(--color-border-tertiary)',
          borderRadius: 6,
        }}
      >
        <Loading />
      </div>
    );
  }

  return (
    <BonusEligibilityTooltip
      rewardOutput={rewardOutput}
      bonusProgram={bonusProgram}
    />
  );
};

/**
 * Shows the status of a given expectation,
 * eg: for the field totalMilesStatus, show a green tick
 */
const BonusQualifiedCellProvider = (params: ICellRendererParams) => {
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const bonusProgram = useBonusCellBonusProgram();
  const elasticDocument = useMemo(() => {
    return params.node.data as ElasticDocument;
  }, [params.node.data]);

  const field = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }

    return colDef.field;
  }, [params]);

  const isPassed = useMemo((): boolean => {
    if (!field) {
      return false;
    }

    return elasticDocument[field];
  }, [elasticDocument, field]);

  const driver = useMemo(() => {
    return elasticDocument['driver'];
  }, [elasticDocument]);

  if (!bonusProgram || params.node.isRowPinned() || !selectedBonusPeriod) {
    return <Cell />;
  }

  return (
    <Cell rightAlign>
      <Tooltip
        isUnstyledTooltip
        content={
          <QualifiedTooltip
            driver={driver}
            bonusProgram={bonusProgram}
            bonusPeriodId={selectedBonusPeriod.id}
          />
        }
      >
        <StatusIcon passed={isPassed} />
      </Tooltip>
    </Cell>
  );
};
const Gate = (params: ICellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<BonusQualifiedCellProvider {...params} />}
  />
);

export default Gate;
